import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  ShieldCheck, UserGear, Users, CaretDown, ArrowClockwise, Check, X,
  MagnifyingGlass, Funnel, Key, Plus, Trash,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  super_admin: 'Super Admin', admin: 'Admin', project_lead: 'Project Lead',
  project_manager: 'Project Manager', supervisor: 'Supervisor', viewer: 'Viewer',
};
const ROLE_COLORS = {
  super_admin: 'bg-red-50 text-red-700 border-red-200',
  admin: 'bg-blue-50 text-blue-700 border-blue-200',
  project_lead: 'bg-purple-50 text-purple-700 border-purple-200',
  project_manager: 'bg-green-50 text-green-700 border-green-200',
  supervisor: 'bg-amber-50 text-amber-700 border-amber-200',
  viewer: 'bg-zinc-50 text-zinc-600 border-zinc-200',
};
const MODULE_LABELS = {
  boq: 'BOQ', work_order: 'Work Orders', purchase_order: 'Purchase Orders',
  financials: 'Financials', execution: 'Execution', action_center: 'Action Center',
  access_control: 'Access Control', user_management: 'User Management', audit: 'Audit',
};

export default function PermissionManager() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [permDefs, setPermDefs] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editPerms, setEditPerms] = useState({});
  const [search, setSearch] = useState('');
  const [showResetDemo, setShowResetDemo] = useState(false);
  const [resettingDemo, setResettingDemo] = useState(false);
  const [delegations, setDelegations] = useState([]);
  const [overrideTypes, setOverrideTypes] = useState([]);
  const [showDelegate, setShowDelegate] = useState(false);
  const [delegForm, setDelegForm] = useState({ delegate_user_id: '', override_type: '' });
  const [savingDeleg, setSavingDeleg] = useState(false);
  const [ewayTurnover, setEwayTurnover] = useState('');
  const [savingEway, setSavingEway] = useState(false);
  const isSuperAdmin = me?.role === 'super_admin';

  const fetchDelegations = useCallback(async () => {
    try {
      const [dRes, tRes, eRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/override-delegations`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/override-delegations/types`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/eway/settings`, { withCredentials: true }),
      ]);
      setDelegations(dRes.data || []);
      setOverrideTypes(tRes.data || []);
      setEwayTurnover(String(eRes.data?.annual_turnover_cr ?? ''));
    } catch { /* non-blocking */ }
  }, []);

  const handleSaveEway = async () => {
    setSavingEway(true);
    try {
      await axios.put(`${BACKEND_URL}/api/eway/settings`, { annual_turnover_cr: parseFloat(ewayTurnover) || 0 }, { withCredentials: true });
      toast.success('E-Way settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSavingEway(false); }
  };

  const handleAddDelegation = async () => {
    if (!delegForm.delegate_user_id || !delegForm.override_type) {
      toast.error('Select a user and an override type');
      return;
    }
    setSavingDeleg(true);
    try {
      await axios.post(`${BACKEND_URL}/api/override-delegations`, delegForm, { withCredentials: true });
      toast.success('Override authority delegated');
      setShowDelegate(false);
      setDelegForm({ delegate_user_id: '', override_type: '' });
      fetchDelegations();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delegate'); }
    finally { setSavingDeleg(false); }
  };

  const handleRevokeDelegation = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/override-delegations/${id}`, { withCredentials: true });
      toast.success('Delegation revoked');
      fetchDelegations();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleResetDemo = async () => {
    setResettingDemo(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/seed-demo`, {}, { withCredentials: true });
      toast.success(`Demo data reset — 4 projects, ${data.observer_alerts_generated} observer alert(s) regenerated`);
      setShowResetDemo(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset demo data');
    } finally { setResettingDemo(false); }
  };

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, permRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/users`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/auth/permissions`, { withCredentials: true }),
      ]);
      setUsers(usersRes.data);
      setPermDefs(permRes.data.permission_defs || {});
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (isSuperAdmin) fetchDelegations(); }, [isSuperAdmin, fetchDelegations]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      toast.success('Role updated');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleSelectUser = (u) => {
    setSelected(u);
    setEditPerms(u.effective_permissions || {});
  };

  const togglePerm = (module, action) => {
    setEditPerms(prev => {
      const current = prev[module] || [];
      const next = current.includes(action) ? current.filter(a => a !== action) : [...current, action];
      return { ...prev, [module]: next };
    });
  };

  const handleSaveOverrides = async () => {
    if (!selected) return;
    try {
      await axios.put(`${BACKEND_URL}/api/admin/users/${selected.id}/permissions`, { permission_overrides: editPerms }, { withCredentials: true });
      toast.success('Permissions saved');
      setSelected(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleResetPerms = async (userId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/users/${userId}/permissions`, { withCredentials: true });
      toast.success('Reset to role defaults');
      setSelected(null);
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const filtered = users.filter(u => !search || u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><div className="animate-pulse text-zinc-500">Loading...</div></div>;

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck size={18} className="text-blue-700" />
              <h1 className="text-base font-bold text-zinc-900">Permission Manager</h1>
            </div>
            <p className="text-xs text-zinc-500">Manage roles and permissions. Toggle overrides per user.</p>
          </div>
          {me?.role === 'super_admin' && (
            <Button onClick={() => setShowResetDemo(true)} disabled={resettingDemo} variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50 rounded-none text-xs" data-testid="reset-demo-button">
              <ArrowClockwise size={15} className={`mr-1.5 ${resettingDemo ? 'animate-spin' : ''}`} />
              {resettingDemo ? 'Resetting…' : 'Reset Demo Data'}
            </Button>
          )}
        </div>
      </header>

      <Dialog open={showResetDemo} onOpenChange={setShowResetDemo}>
        <DialogContent data-testid="reset-demo-dialog">
          <DialogHeader>
            <DialogTitle>Reset Demo Data</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-600">This will reset all 4 demo projects to their clean seed state. Continue?</p>
          <p className="text-[11px] text-zinc-400">Only demo records are affected — your real projects are never touched.</p>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setShowResetDemo(false)} data-testid="reset-demo-cancel">Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 rounded-none" onClick={handleResetDemo} disabled={resettingDemo} data-testid="reset-demo-confirm">
              {resettingDemo ? 'Resetting…' : 'Reset Demo Data'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delegate Override Authority (Super Admin) */}
      {isSuperAdmin && (
        <div className="max-w-5xl mx-auto px-4 pt-4">
          <div className="bg-white border border-zinc-200" data-testid="override-delegation-panel">
            <div className="p-4 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Key size={16} className="text-violet-700" weight="fill" />
                <div>
                  <h2 className="text-sm font-bold text-zinc-900">Delegate Override Authority</h2>
                  <p className="text-[11px] text-zinc-500">Assign specific override powers to named users. Delegated users can perform only their assigned overrides.</p>
                </div>
              </div>
              <Button onClick={() => setShowDelegate(true)} className="rounded-none h-7 text-xs px-3 bg-violet-700 hover:bg-violet-800 text-white" data-testid="add-delegation-btn">
                <Plus size={13} className="mr-1" /> Delegate
              </Button>
            </div>
            <div className="p-4">
              {delegations.length === 0 ? (
                <p className="text-xs text-zinc-400 py-2">No override authority delegated. Super Admin retains all overrides.</p>
              ) : (
                <div className="space-y-1.5">
                  {delegations.map(d => (
                    <div key={d.id} className="flex items-center justify-between border border-zinc-200 px-3 py-2 text-xs" data-testid={`delegation-row-${d.id}`}>
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-zinc-800">{d.delegate_name || d.delegate_email}</span>
                        <span className="text-[10px] font-bold px-1.5 py-0.5 border bg-violet-50 text-violet-700 border-violet-200">{d.override_type_label}</span>
                        <span className="text-zinc-400 text-[10px]">by {d.granted_by_name}</span>
                      </div>
                      <button onClick={() => handleRevokeDelegation(d.id)} className="text-red-600 hover:text-red-700 flex items-center gap-1 text-[11px]" data-testid={`revoke-delegation-${d.id}`}>
                        <Trash size={13} /> Revoke
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* E-Way Bill Settings (Super Admin) */}
      {isSuperAdmin && (
        <div className="max-w-5xl mx-auto px-4 pt-4">
          <div className="bg-white border border-zinc-200" data-testid="eway-settings-panel">
            <div className="p-4 border-b border-zinc-200 bg-zinc-50">
              <h2 className="text-sm font-bold text-zinc-900">E-Way Bill Compliance Settings</h2>
              <p className="text-[11px] text-zinc-500">Company annual turnover drives the 6-digit HSN requirement (mandatory when turnover exceeds ₹5 Cr).</p>
            </div>
            <div className="p-4 flex items-end gap-3">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wide text-zinc-500">Annual Turnover (₹ Cr)</label>
                <Input type="number" value={ewayTurnover} onChange={(e) => setEwayTurnover(e.target.value)}
                  className="mt-1 rounded-none border-zinc-300 h-8 text-sm w-40" placeholder="e.g. 6" data-testid="eway-turnover-input" />
              </div>
              <Button onClick={handleSaveEway} disabled={savingEway} className="rounded-none h-8 text-xs bg-blue-700 hover:bg-blue-800 text-white" data-testid="eway-save-btn">
                {savingEway ? 'Saving…' : 'Save'}
              </Button>
              {parseFloat(ewayTurnover) > 5 && (
                <span className="text-[11px] text-amber-700 pb-1.5">6-digit HSN required on documents.</span>
              )}
            </div>
          </div>
        </div>
      )}

      <Dialog open={showDelegate} onOpenChange={setShowDelegate}>
        <DialogContent data-testid="delegate-dialog">
          <DialogHeader>
            <DialogTitle>Delegate Override Authority</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wide text-zinc-500">User</label>
              <select value={delegForm.delegate_user_id} onChange={(e) => setDelegForm(f => ({ ...f, delegate_user_id: e.target.value }))}
                className="w-full mt-1 text-xs border border-zinc-300 px-2 py-1.5 bg-white" data-testid="delegate-user-select">
                <option value="">— Select user —</option>
                {users.filter(u => u.role !== 'super_admin').map(u => (
                  <option key={u.id} value={u.id}>{u.name || u.email} ({ROLE_LABELS[u.effective_role] || u.role})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wide text-zinc-500">Override Type</label>
              <select value={delegForm.override_type} onChange={(e) => setDelegForm(f => ({ ...f, override_type: e.target.value }))}
                className="w-full mt-1 text-xs border border-zinc-300 px-2 py-1.5 bg-white" data-testid="delegate-type-select">
                <option value="">— Select override type —</option>
                {overrideTypes.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setShowDelegate(false)} data-testid="delegate-cancel">Cancel</Button>
            <Button className="bg-violet-700 hover:bg-violet-800 rounded-none" onClick={handleAddDelegation} disabled={savingDeleg} data-testid="delegate-confirm">
              {savingDeleg ? 'Saving…' : 'Delegate Authority'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <main className="max-w-5xl mx-auto px-4 py-5 flex gap-5">
        {/* User List */}
        <div className="w-80 flex-shrink-0 space-y-3">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              className="rounded-none border-zinc-300 h-8 text-xs pl-8" placeholder="Search users..." data-testid="user-search" />
          </div>
          <div className="space-y-1 max-h-[600px] overflow-y-auto">
            {filtered.map(u => (
              <button key={u.id} onClick={() => handleSelectUser(u)}
                className={`w-full text-left p-2.5 border text-xs transition-colors ${selected?.id === u.id ? 'bg-blue-50 border-blue-300' : 'bg-white border-zinc-200 hover:bg-zinc-50'}`}
                data-testid={`user-card-${u.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-zinc-800">{u.name || u.email}</span>
                  {u.has_overrides && <span className="text-[8px] bg-amber-100 text-amber-700 px-1 py-0.5 font-bold">CUSTOM</span>}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 border ${ROLE_COLORS[u.effective_role] || 'bg-zinc-50 text-zinc-600 border-zinc-200'}`}>
                    {ROLE_LABELS[u.effective_role] || u.role}
                  </span>
                  <span className="text-zinc-400 text-[9px]">{u.email}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Permission Editor */}
        <div className="flex-1">
          {!selected ? (
            <div className="bg-white border border-zinc-200 p-8 text-center">
              <UserGear size={32} className="mx-auto text-zinc-300 mb-2" />
              <p className="text-sm text-zinc-500">Select a user to view or edit permissions</p>
            </div>
          ) : (
            <div className="bg-white border border-zinc-200" data-testid="permission-editor">
              <div className="p-4 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-bold text-zinc-900">{selected.name || selected.email}</h2>
                  <div className="flex items-center gap-2 mt-1">
                    <select value={selected.role} onChange={(e) => handleRoleChange(selected.id, e.target.value)}
                      className="text-[10px] font-bold border px-2 py-0.5 bg-white" data-testid="role-select">
                      {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex gap-2">
                  {selected.has_overrides && (
                    <Button onClick={() => handleResetPerms(selected.id)} variant="outline" className="rounded-none h-7 text-xs px-2 border-amber-300 text-amber-700" data-testid="reset-perms-btn">
                      <ArrowClockwise size={12} className="mr-1" /> Reset to Default
                    </Button>
                  )}
                  <Button onClick={handleSaveOverrides} className="rounded-none h-7 text-xs px-3 bg-blue-700 hover:bg-blue-800 text-white" data-testid="save-perms-btn">
                    Save Overrides
                  </Button>
                </div>
              </div>
              <div className="p-4 space-y-4 max-h-[500px] overflow-y-auto">
                {Object.entries(permDefs).map(([module, actions]) => (
                  <div key={module} className="border border-zinc-100 p-3">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-2">{MODULE_LABELS[module] || module}</p>
                    <div className="flex flex-wrap gap-2">
                      {actions.map(action => {
                        const active = (editPerms[module] || []).includes(action);
                        return (
                          <button key={action} onClick={() => togglePerm(module, action)}
                            className={`flex items-center gap-1 text-[10px] font-semibold px-2 py-1 border transition-colors ${
                              active ? 'bg-green-50 border-green-300 text-green-700' : 'bg-zinc-50 border-zinc-200 text-zinc-400'
                            }`}
                            data-testid={`perm-${module}-${action}`}>
                            {active ? <Check size={10} /> : <X size={10} />}
                            {action.replace(/_/g, ' ')}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
